import React, { useEffect, useState } from "react";
import "./ShopDetails.css";
import { useDispatch, useSelector } from "react-redux";
import Filter from "../Filters/Filter";
import { Link, useNavigate } from "react-router-dom";
import { FaAngleRight, FaAngleLeft } from "react-icons/fa6";
import { FaCartPlus } from "react-icons/fa";
import {
  fetchProductList,
  filterProductsByType,
} from "../../../Redux/productSlice";
import ProductSkeleton from "../../Skeleton/ProductSkeleton";
import { addProductToCart, productListOfCart } from "../../../Redux/cartSlice";
import useSnackbar from "../../../hooks/useSnackbar";
import SnackbarComponent from "../../Snackbar";
import { formatCurrency } from "../../../Utils/formatCurrency";

const ShopDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const { products, loading, selectedProductType } = useSelector(
    (state) => state.product
  );
  const { user, isAuthenticated, logged } = useSelector((state) => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [debouncedQuery, setDebouncedQuery] = useState(""); // Debounced query state

  const limit = 12;

  useEffect(() => {
    let params = {
      limit,
      skip: (currentPage - 1) * limit,
      sort: "created_at",
      sortType: "desc",
      search: debouncedQuery,
    };
    if (selectedProductType) params.product_type = selectedProductType;
    dispatch(fetchProductList(params)).then((response) => {
      setHasNextPage(response.payload.isNext);
    });
    scrollToTop();
  }, [dispatch, currentPage, selectedProductType, debouncedQuery]);

  // Debounce the search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery); // Update debouncedQuery after delay
    }, 800); // Delay of 500ms

    return () => {
      clearTimeout(handler); // Clear timeout if input changes within delay
    };
  }, [searchQuery]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleSearch = (event) => {
    dispatch(filterProductsByType(null));
    setSearchQuery(event.target.value);
  };

  const handleAddToCart = (product) => {
    let quantity = product.variants[0].inventory_quantity || 0;
    let variantId = product.variants[0].id;

    if (user?.success && isAuthenticated && logged === "pass") {
      if (quantity > 0) {
        const productDetails = {
          productId: product._id,
          variantId: variantId,
          quantity: 1,
        };
        dispatch(addProductToCart(productDetails))
          .then(({ payload }) => {
            showSnackbar(
              payload.message || "Added to cart!",
              payload.success ? "success" : "error"
            );
            dispatch(productListOfCart());
          })
          .catch((error) => {
            showSnackbar(
              error.message || "Failed to added into cart!",
              "error"
            );
          });
      } else {
        showSnackbar("Please add quantity!", "warning");
      }
    } else {
      showSnackbar(
        "You must log in first to add products to your shopping cart!",
        "warning"
      );
      setTimeout(() => {
        navigate("/auth");
      }, 1000);
    }
  };

  const getProductPrice = (product) => {
    const prod_var = product.variants[0];

    const unitPrice =
      prod_var?.userPrice != null
        ? Number(prod_var.userPrice)
        : Number(prod_var?.price || 0);

    const current_price = unitPrice || 0;

    return formatCurrency(current_price);
  };

  const getProductPackSize = (product) => {
    const pack_size = product?.pack_size;
    const price_per_pack = product?.price_per_pack;

    return (
      <React.Fragment>
        <span className="sdProductPackSize">Pack Size of {pack_size}</span>
        <span className="sdProductPackAmount">
          {formatCurrency(price_per_pack)}
        </span>
      </React.Fragment>
    );
  };

  return (
    <div>
      <div className="shopDetails">
        <div className="shopDetailMain">
          <div className="shopDetails__left">
            <Filter />
          </div>
          <div className="shopDetails__right">
            <div className="shopDetailsSorting">
              <div className="shopDetailsBreadcrumbLink">
                <Link to="/" onClick={scrollToTop}>
                  Home
                </Link>
                &nbsp;/&nbsp;
                <Link to="/Product">The Shop</Link>
              </div>
              <div className="shopDetailsSearchBox">
                <input
                  type="text"
                  className="searchInput"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
            </div>
            <div className="shopDetailsProducts">
              <div className="shopDetailsProductsContainer">
                {loading ? (
                  <ProductSkeleton />
                ) : (
                  products &&
                  products.map((product) => (
                    <div className="sdProductContainer" key={product.id}>
                      <div className="sdProductImages">
                        <Link
                          to={`/product/${product._id}`}
                          onClick={scrollToTop}
                        >
                          <img
                            src={product.images[0]?.src}
                            alt={product.title}
                            // className="sdProduct_front"
                          />
                          {product?.images[1] && product?.images[1]?.src && (
                            <img
                              src={product.images[1]?.src}
                              alt={product.title}
                              className="sdProduct_back"
                            />
                          )}
                        </Link>

                        {product?.variants?.length > 1 ? (
                          <Link
                            className="quick_link"
                            to={`/product/${product._id}`}
                          >
                            Quick Shop
                          </Link>
                        ) : product?.variants[0]?.inventory_quantity > 0 ? (
                          <h4 onClick={() => handleAddToCart(product)}>
                            Add to Cart
                          </h4>
                        ) : (
                          <h4>Sold Out</h4>
                        )}
                      </div>

                      <div className="sdProductInfo">
                        <div className="sdProductCategoryWishlist">
                          <p>{product.product_type}</p>
                        </div>
                        <div className="sdProductNameInfo">
                          <Link
                            to={`/product/${product._id}`}
                            onClick={scrollToTop}
                          >
                            <h5>{product.title}</h5>
                          </Link>

                          <p>
                            {getProductPrice(product)}{" "}
                            {product.pack_size > 0 ? "Each" : ""} <br />
                            {product.pack_size && product.pack_size > 0 && (
                              <span className="sdProductPackInfo">
                                {getProductPackSize(product)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="shopDetailsPagination">
              <div
                className={
                  currentPage === 1
                    ? "sdPaginationPrev disabled"
                    : "sdPaginationPrev"
                }
                onClick={currentPage > 1 ? handlePrevPage : null}
              >
                <p>
                  <FaAngleLeft />
                  Prev
                </p>
              </div>
              <div className="sdPaginationNumber">
                <div className="paginationNum">{currentPage}</div>
              </div>
              <div
                className={
                  !hasNextPage
                    ? "sdPaginationNext disabled"
                    : "sdPaginationNext"
                }
                onClick={hasNextPage ? handleNextPage : null}
              >
                <p>
                  Next
                  <FaAngleRight />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SnackbarComponent
        message={snackbar.message}
        severity={snackbar.severity}
        open={snackbar.open}
        onClose={hideSnackbar}
      />
    </div>
  );
};

export default ShopDetails;
